import React, { memo, Suspense, useEffect, useReducer, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { isMobile } from 'react-device-detect';
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Container,
    Grid,
    IconButton,
    Link,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
} from '@mui/material';
import {
    Article, CarCrash,
    DirectionsBoat,
    DirectionsCar,
    Download, Fax,
    MoreVert,
    PictureAsPdf, ThumbUp,
    WarningAmber
} from '@mui/icons-material';
import EditableInput from 'components/common/EditableInput';
import Currency from 'components/formaters/Currency';
import Date from 'components/formaters/Date';
import { isFProject } from 'config/project';
import { colors } from 'config/invoicePackages';
import useContextMenu from 'hooks/useContextMenu';
import { CompanyModel, CompanyType } from 'models/CompanyModel';
import { PartyType } from 'models/PartyModel';
import { CarModel, ExportType, PriceTerms, StockStatus } from 'models/StockModel';
import { VoyageModel, VoyageStatus } from 'models/VoyageModel';
import ProblemMark from 'modules/cars/common/ProblemMark';
import PartyButton from 'modules/cars/common/PartyButton';
import CarCargoCell from 'modules/cars/common/CarCargoCell';
import CarDocCell from 'modules/cars/common/CarDocCell';
import CopyableChassis from 'modules/cars/common/CopyableChassis';
import AlbumButton from 'modules/cars/common/AlbumButton';
import useCarModals from 'modules/cars/hooks/useCarModals';
import useSyncCars from 'modules/cars/hooks/useSyncCars';
import useCarDocModals from 'modules/cars/hooks/useCarDocModals';
import { exportCertificateRequired } from 'modules/cars/utils/common';
import useVoyages from 'modules/voyages/hooks/useVoyages';
import useVoyageDocs from 'modules/voyages/hooks/useVoyageDocs';
import useVoyageModals from 'modules/voyages/hooks/useVoyageModals';
import { PriceWithInvoice } from 'modules/voyages/common/FinanceCells';
import auth from 'recoil/atoms/auth';
import Api from 'utils/api';
import { arrayGrouped, padBetween } from 'utils/common';


const VoyageInner = () => {
    const {t} = useTranslation('cars');
    const {id} = useParams();
    const my = useRecoilValue(auth);
    const navigate = useNavigate();
    const [voyage, setVoyage] = useState<VoyageModel>();
    const [sortByBatch, setSortByBatch] = useReducer(sort => !sort, false);
    const {getVoyage, voyageDeparted} = useVoyages();
    const {
        downloadManifest,
        downloadBLs,
        downloadECs,
        downloadInvoices,
        downloadSI,
        downloadDismantledList,
        downloadDocPackage,
        downloadED,
        downloadCargoLists,
        downloadTallymanList,
        downloadDeliveryList,
    } = useVoyageDocs();
    const {
        openUploadEDModal,
        openJPInvoiceSplitterModal,
        confirmResetInvoices,
        openToyamaInvoicePackagesModal,
        openJpInvoicePackagesModal,
        openTransferCarsDialog,
        loadVoyageWithConfirmation,
    } = useVoyageModals();
    const {
        confirmCancelBooking,
        openInnerCargoModal,
        openEditCarModal,
        confirmRemoveCar,
    } = useCarModals();
    const [cars, setCars] = useState<CarModel[]>([]);

    useSyncCars(setCars);

    // TODO merge with main voyage list
    const {openMenu: openDocs} = useContextMenu<VoyageModel>(({value, close}) => {
        const button = (label: string, action: (id: number) => void) => (
            <MenuItem
                key={label}
                onClick={() => {
                    action(value.id);
                    close();
                }}
            >
                {t(label)}
            </MenuItem>
        );

        const act = [];

        if (my.type === CompanyType.Master && value.status !== VoyageStatus.Lost) {
            act.push(button('full_jp_package', downloadDocPackage()));

            if (!isFProject)
                act.push(button('jp_bls', downloadBLs('jp')));

            if (value.pol.id === 1)
                act.push(button('toyama_invoices', openToyamaInvoicePackagesModal));
            else if (value.pol.id === 9)
                act.push(button('naoetsu_invoices', downloadInvoices(undefined, 'naoetsu')));
            else
                act.push(button('jp_invoices', openJpInvoicePackagesModal));

            if (value.pod.country === 'KR' && (value.status === VoyageStatus.Departed || value.status === VoyageStatus.Arrived))
                act.push(button('korea_invoices', downloadInvoices(undefined, 'korea')));

            if (value.pol.id === 1 || value.pol.id === 4 || value.pol.id === 7) {
                act.push(button(t('loading_cargo_lists'), downloadCargoLists('common')));
                act.push(button('Delivery Fax Cars With Docs', downloadCargoLists('doc-no_yard')));
                act.push(button('Documents Fax Cars in Yard', downloadCargoLists('no_doc-yard')));
            }

            act.push(button('tallyman_list', downloadTallymanList));
            act.push(button('delivery_list', downloadDeliveryList));

            act.push(button('jp_si', downloadSI()));
            act.push(button('ecs', downloadECs));

            if (!isFProject) {
                act.push(button('ru_manifest', downloadManifest()));
                act.push(button('ru_manifest_warehouse', downloadManifest('warehouse')));
                act.push(button('ru_bls', downloadBLs('ru')));
            }
            value.allowDismantled && act.push(button('dismantled_list', downloadDismantledList));
        }

        if (my.type === CompanyType.Party) {
            //act.push(button('ru_bls', downloadBLs('ru')));
            act.push(button('ecs', downloadECs));
        }

        if (my.type === CompanyType.Shipper && value.eds && value.eds[my.company.id]) {
            act.push(button('bl_ed', downloadDocPackage(my.company.id)));
            act.push(button('ed', downloadED(my.company.id)));
        }

        if (my.type === CompanyType.PortAgent) {
            act.push(button('full_jp_package', downloadDocPackage()));
            act.push(button('ecs', downloadECs));
        }

        if (my.type === CompanyType.VesselOwner) {
            act.push(button('full_jp_package', downloadDocPackage()));

            if (value.pod.country === 'KR' && (value.status === VoyageStatus.Departed || value.status === VoyageStatus.Arrived))
                act.push(button('korea_invoices', downloadInvoices(undefined, 'korea')));

            act.push(button('ru_manifest', downloadManifest()));
        }

        return act;
    });

    const {openMenu: openManage} = useContextMenu<CarModel>(({value, close}) => [
        (my.permissions.has('cars update') && <MenuItem
            key="edit"
            onClick={() => {
                openEditCarModal(value);
                close();
            }}
        >
            {t('edit_car')}
        </MenuItem>),

        (voyage!.status === VoyageStatus.Pending &&
            (value.status === StockStatus.Pending || value.status === StockStatus.Ready) &&
            my.permissions.has('booking cancel') &&
            <MenuItem
                key="cancel"
                onClick={() => {
                    confirmCancelBooking([value]).then(load);
                    close();
                }}
            >
                {t('cancel_booking')}
            </MenuItem>
        ),

        (voyage!.status === VoyageStatus.Pending &&
            voyage!.pol.country !== 'JP' &&
            my.permissions.has('booking cancel') &&
            <MenuItem
                key="cancel"
                onClick={() => {
                    Api.voyages.cancelTransferCars(voyage!.id, [value.id]).then(load);
                    close();
                }}
            >
                {t('cancel')}
            </MenuItem>
        ),

        (my.permissions.has('cars delete') && <MenuItem
            key="delete"
            onClick={() => {
                confirmRemoveCar(value).then(load);
                close();
            }}
            sx={{color: 'error.main'}}
        >
            {t('delete_forever')}
        </MenuItem>),
    ]);

    const load = () => {
        getVoyage(Number(id))
            .then(res => setVoyage(res.data))
            .catch(() => navigate('/voyages'));

        Api.cars.voyage(Number(id), true)
            .then(data => {
                setCars(data.data);
            });
    }

    // eslint-disable-next-line
    useEffect(load, [id]);

    if (!voyage)
        return null;

    const groups = arrayGrouped(cars, 'company.id');
    const payloadSpace = cars.reduce((sum, c) => {
        if (exportCertificateRequired(c) && !c.documents.exportCertificate)
            return sum;

        return sum + Math.max((c.length || 0) * (c.width || 0) / 10000, 9.98);
    }, 0);

    if (sortByBatch) {
        for (const cid in groups) {
            groups[cid].sort((a, b) =>
                (a.jpInvoice?.package || 0) - (b.jpInvoice?.package || 0) ||
                (a.jpInvoice?.batch || 0) - (b.jpInvoice?.batch || 0) ||
                (a.jpInvoice?.pos || 0) - (b.jpInvoice?.pos || 0)
            )
        }
    } else {
        for (const cid in groups) {
            groups[cid].sort((a, b) =>
                a.priceTerms?.localeCompare(b.priceTerms || '') ||
                (a.parties.dealer?.id || 0) - (b.parties.dealer?.id || 0) ||
                a.vehicleType?.localeCompare(b.vehicleType || '') ||
                b.fuel?.localeCompare(a.fuel || '') || 0
            )
        }
    }

    let i = 1;

    const mainAction = (
        <>
            {my.permissions.has('cars manage') && (
                <Button
                    component={Link}
                    size="small"
                    href={`/voyage/${voyage.id}/bl`}
                >
                    BL
                </Button>
            )}
            {' '}

            {my.permissions.has('invoices create') && !isFProject && (
                <Button
                    component={Link}
                    size="small"
                    href={`/voyage/${voyage.id}/finances`}
                >
                    {t('finances')}
                </Button>
            )}
            {' '}

            {my.permissions.has('cars manage') && <>
                <Button size="small" onClick={() => openUploadEDModal(voyage).then(load)}>{t('upload_ed')}</Button>
                {' '}
                <Button size="small" onClick={() => confirmResetInvoices(voyage)}>{t('reset_invoices')}</Button>
            </>}

            <IconButton
                aria-label="voyage docs"
                onClick={e => openDocs(e, voyage)}
            >
                <Download/>
            </IconButton>
        </>
    );

    const loadFromPort = () => {
        Api.cars.port(voyage.pol.code)
            .then(data => openTransferCarsDialog(voyage, data.data))
            .then(load);
    };

    const showColumns = {
        tallyNum : voyage.pol.id === 1 && (my.type === CompanyType.Master || my.type === CompanyType.PortAgent),
        jpInvBatch : voyage.pol.id !== 1 && my.permissions.has('cars manage'),
        consignee: my.type !== CompanyType.PortAgent && my.type !== CompanyType.VesselOwner && !isFProject,
        notify: my.type !== CompanyType.PortAgent && my.type !== CompanyType.VesselOwner && !isFProject,
        dealer: my.type !== CompanyType.PortAgent && my.type !== CompanyType.VesselOwner && !isFProject,
        freight: my.type !== CompanyType.PortAgent,
    };

    return (
        <Container maxWidth={false} sx={{mt: 2}}>
            <Card sx={{mb: 2}}>
                <CardHeader
                    title={buildTitle(t, `${t('voyage')} ${voyage.vessel.name} ${voyage.number}`, cars, voyage.customsYard?.name, true)}
                    action={mainAction}
                />
                <CardContent>
                    <Grid container columnSpacing={2}>
                        <Grid item xs={12} sm={4} container>
                            <Grid item xs={4}>
                                {voyage.pol.name}<br/>
                                <small><Date date={voyage.departedAt || voyage.etd}/></small>
                            </Grid>

                            <Grid item xs={4} textAlign="center" textTransform="uppercase">
                                {voyage.status} <br/>
                                {my.permissions.has('cars manage') && (
                                    <>
                                        {voyage.status === VoyageStatus.Pending && voyage.pol.country !== 'JP' && (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="secondary"
                                                onClick={loadFromPort}
                                                sx={{mr: 2}}
                                            >
                                                {t('load_cars')}
                                            </Button>
                                        )}

                                        {voyage.status === VoyageStatus.Pending && (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="success"
                                                onClick={() => loadVoyageWithConfirmation(voyage.id)}
                                            >
                                                {t('voyage_loaded')}
                                            </Button>
                                        )}

                                        {voyage.status === VoyageStatus.Loaded && (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="success"
                                                onClick={() => voyageDeparted(voyage.id)}
                                            >
                                                {t('voyage_departed')}
                                            </Button>
                                        )}
                                    </>
                                )}
                            </Grid>

                            <Grid item xs={4} textAlign="right">
                                {voyage.pod.name} <br/>
                                <small><Date date={voyage.arrivedAt || voyage.eta}/></small>
                            </Grid>
                        </Grid>

                        <Grid item xs={6} sm={2} textAlign="right">
                            {my.type === CompanyType.Master && (
                                <>
                                    {t('vessel')}: {(voyage.vessel.space || 0).toFixed(2)}m<sup>2</sup> <br/>
                                    {t('cars')}: {payloadSpace.toFixed(2)}m<sup>2</sup>
                                </>
                            )}
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {Object.values(groups).map(group => (
                <Card sx={{mb: 2}} key={group[0].company.id}>
                    <CardHeader
                        title={buildTitle(t, group[0].company.name, group)}
                        action={
                            <>
                                {my.type === CompanyType.Master && <RequestFaxes voyage={voyage} group={group} />}
                                {(my.type === CompanyType.Master || my.type === CompanyType.Shipper) && <RequestDownloads voyage={voyage} group={group} />}

                                {voyage.eds && voyage.eds[group[0].company.id] && (my.type === CompanyType.Master || my.type === CompanyType.Shipper) && (
                                    <>
                                        <Button
                                            size="small"
                                            onClick={() => downloadDocPackage(group[0].company.id)(voyage.id)}
                                        >
                                            {t('bl_ed')}
                                        </Button>
                                    </>
                                )}

                                {my.permissions.has('cars manage') && (
                                    <Button
                                        size="small"
                                        onClick={() => openJPInvoiceSplitterModal(voyage, group[0].company as CompanyModel, group).then(load)}
                                    >
                                        {t('split_invoices')}
                                    </Button>
                                )}

                            </>
                        }
                    />

                    <TableContainer>
                        <Table
                            size="small"
                            sx={{
                                'tbody tr:hover': {
                                    backgroundColor: 'action.hover',
                                },
                                '.MuiTableCell-sizeSmall': {
                                    padding: '6px',
                                    '&:first-of-type': {
                                        paddingLeft: 2,
                                    }
                                },
                            }}
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{width: 50}}>#</TableCell>
                                    {showColumns.tallyNum &&
                                        <TableCell sx={{width: 90}}>{t('tally_number')}</TableCell>
                                    }
                                    {showColumns.jpInvBatch &&
                                        <>
                                            <TableCell sx={{width: 90}} onClick={setSortByBatch}>{t('batch')} {sortByBatch ? '↕' : '↓'}</TableCell>
                                            <TableCell sx={{width: 90}}>{t('pos')}</TableCell>
                                        </>
                                    }
                                    <TableCell sx={{width: '10%'}}>{t('model')}</TableCell>
                                    <TableCell sx={{width: 120}}>{t('docs')}</TableCell>
                                    <TableCell sx={{width: 210}}>{t('chassis')}</TableCell>
                                    <TableCell sx={{width: 80}}>{t('problems')}</TableCell>
                                    <TableCell sx={{width: 170}}>{t('unit_price')}</TableCell>
                                    <TableCell sx={{width: 40}}>{t('fuel')}</TableCell>

                                    {showColumns.consignee &&
                                        <TableCell>{t('consignees')}</TableCell>
                                    }
                                    {showColumns.notify &&
                                        <TableCell>{t('notifies')}</TableCell>
                                    }
                                    {showColumns.dealer &&
                                        <TableCell>{t('dealers')}</TableCell>
                                    }

                                    <TableCell>{t('cargo')}</TableCell>
                                    <TableCell>{t('photos')}</TableCell>

                                    {showColumns.freight &&
                                        <TableCell>{t('freight')}</TableCell>
                                    }

                                    <TableCell sx={{width: 42}}></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {group.map(car => (
                                    <CarRow
                                        key={car.id}
                                        car={car}
                                        i={i++}
                                        action={e => openManage(e, car)}
                                        openInnerCargoModal={openInnerCargoModal}
                                        voyage={voyage}
                                        showColumns={showColumns}
                                    />
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                </Card>
            ))}
        </Container>
    );
};


type DropdownCarListsPros = {
    voyage: VoyageModel,
    group: CarModel[],
}

const RequestFaxes = ({voyage, group}: DropdownCarListsPros) => {
    const {t} = useTranslation('cars');
    const {openDeliveryFaxModal} = useVoyageModals();
    const {noDoc_noYard, doc_noYard, noDoc_yard} = missingCounts(group);

    const {openMenu} = useContextMenu<VoyageModel>(({value, close}) => {
        const button = (label: string, type: string) => (
            <MenuItem
                key={label}
                onClick={() => {
                    openDeliveryFaxModal(voyage, group[0].company as CompanyModel, type);
                    close();
                }}
            >
                {label}
            </MenuItem>
        );

        const act = [];

        if (noDoc_noYard)
            act.push(button(t('no_cars_docs'), 'no_doc-no_yard'));

        if (doc_noYard)
            act.push(button(t('no_cars'), 'doc-no_yard'));

        if (noDoc_yard)
            act.push(button(t('no_docs'), 'no_doc-yard'));

        return act;
    });

    if (!noDoc_noYard && !doc_noYard && !noDoc_yard)
        return null;

    return (
        <Button
            startIcon={<Fax/>}
            size="small"
            onClick={e => openMenu(e)}
        >
            {t('fax')}
        </Button>
    )
};

const RequestDownloads = ({voyage, group}: DropdownCarListsPros) => {
    const {t} = useTranslation('cars');
    const {downloadCargoLists} = useVoyageDocs();
    const {noDoc_noYard, doc_noYard, noDoc_yard} = missingCounts(group);

    const {openMenu} = useContextMenu<VoyageModel>(({value, close}) => {
        const button = (label: string, type: string) => (
            <MenuItem
                key={label}
                onClick={() => {
                    downloadCargoLists(type, group[0].company.id)(value.id);
                    close();
                }}
            >
                {label}
            </MenuItem>
        );

        const act = [];

        if (noDoc_noYard)
            act.push(button(t('no_cars_docs'), 'no_doc-no_yard'));

        if (doc_noYard)
            act.push(button(t('no_cars'), 'doc-no_yard'));

        if (noDoc_yard)
            act.push(button(t('no_docs'), 'no_doc-yard'));

        return act;
    });

    if (!noDoc_noYard && !doc_noYard && !noDoc_yard)
        return null;

    return (
        <Button
            startIcon={<Download/>}
            size="small"
            onClick={e => openMenu(e, voyage)}
        >
            {t('download')}
        </Button>
    )
};

const missingCounts = (cars: CarModel[]) => {
    let noDoc_noYard = 0,
        doc_noYard = 0,
        noDoc_yard = 0;

    cars.forEach(c => {
        const copy =
            c.documents.exportCertificate || c.documents.vehicleInvoice || c.documents.vehicleCatalog;

        if (!c.yard?.arrived && !copy)
            noDoc_noYard++;

        if (!c.yard?.arrived && copy)
            doc_noYard++;

        if (c.yard?.arrived && !copy)
            noDoc_yard++;
    });

    return {
        noDoc_noYard,
        doc_noYard,
        noDoc_yard,
    }
};


const buildTitle = (t: TFunction, start: string, cars: CarModel[], customsYard?: string, main?: boolean) => {
    let in_port = 0,
        with_docs = 0,
        with_original = 0,
        without_docs = 0,
        dismantled = 0,
        in_port_with_docs = 0;

    const withoutDocsPerCompany: Record<string, number> = {};

    cars.forEach(c => {
        if (c.tallyNumber || (c.yard?.portId !== 1 && c.yard?.arrived))
            in_port++;

        const hasScanned = exportCertificateRequired(c) ?
            c.documents.exportCertificate :
            (c.documents.vehicleInvoice || c.documents.vehicleCatalog);

        if (hasScanned)
            with_docs++;

        if (c.exportCertificate?.originalArrivedAt)
            with_original++;

        if (c.yard?.portId === 1 ? (c.tallyNumber && c.exportCertificate?.originalArrivedAt) : (c.yard?.arrived && hasScanned))
            in_port_with_docs++;

        if (!hasScanned && !c.exportCertificate?.originalArrivedAt) {
            without_docs++;

            if (main)
                withoutDocsPerCompany[c.company.name] = (withoutDocsPerCompany[c.company.name] || 0) + 1;
        }

        if (c.exportType !== ExportType.Car)
            dismantled++
    });

    return (
        <Box display="flex" flexWrap="wrap" alignItems="center" gap={1}>
            <span>
                {start}
            </span>

            {isMobile && <Box width="100%"/>}

            <Tooltip
                title={t('cars_total')}
                placement="top"
            >
                <span>
                    <DirectionsBoat
                        fontSize="small"
                    />
                    {cars.length}
                </span>
            </Tooltip>

            {dismantled > 0 && (
                <Tooltip
                    title={t('cars_dismantled')}
                    placement="top"
                >
                    <span>
                        <CarCrash
                            fontSize="small"
                            color="error"
                        />
                        {dismantled}
                    </span>
                </Tooltip>
            )}

            {in_port > 0 && (
                <Tooltip
                    title={t('cars_in_port')}
                    placement="top"
                >
                    <span>
                        <DirectionsCar
                            fontSize="small"
                        />
                        {in_port}
                    </span>
                </Tooltip>
            )}

            <Tooltip
                title={t('cars_with_scan')}
                placement="top"
            >
                <span>
                    <PictureAsPdf
                        fontSize="small"
                    />
                    {with_docs}
                </span>
            </Tooltip>

            {with_original > 0 && (
                <Tooltip
                    title={t('cars_with_original')}
                    placement="top"
                >
                    <span>
                        <Article
                            fontSize="small"
                        />
                        {with_original}
                        {' '}
                    </span>
                </Tooltip>
            )}

            {without_docs > 0 && (
                <Tooltip
                    title={main ? <WithoutDocsTooltip
                        items={Object.entries(withoutDocsPerCompany)}
                        total={without_docs}
                    /> : t('cars_without_doc')}
                    placement="top"
                >
                    <span>
                        <Article
                            fontSize="small"
                            color="error"
                        />
                        {without_docs}
                        {' '}
                    </span>
                </Tooltip>
            )}

            {in_port_with_docs > 0 && (
                <Tooltip
                    title={t('cars_in_port_with_docs')}
                    placement="top"
                >
                    <span>
                        <ThumbUp
                            fontSize="small"
                            color="success"
                        />
                        {in_port_with_docs}
                    </span>
                </Tooltip>
            )}

            {customsYard && (
                <Box component="span" color="secondary.main">Yard {customsYard}</Box>
            )}
        </Box>
    );
};


type WithoutDocsTooltipProps = {
    items: [string, number][],
    total: number,
}

const WithoutDocsTooltip = ({items, total}: WithoutDocsTooltipProps) => {
    const {t} = useTranslation();

    const longest = items.reduce((acc, item) => Math.max(acc, item[0].length), 0);

    return (
        <>
            {t('cars_without_doc')}<br/>
            {items.map(([name, count]) => (
                <Box key={name} sx={{fontFamily: 'monospace'}}>{padBetween(name, longest, count.toString(), '\u00A0')}</Box>
            ))}
            {t('total')}: {total}
        </>
    )
};


type CarRowProps = {
    car: CarModel,
    i: number,
    voyage: VoyageModel,
    action: React.MouseEventHandler<HTMLButtonElement>,
    openInnerCargoModal: any,
    showColumns: { [key in 'tallyNum' | 'consignee' | 'notify' | 'dealer' | 'freight' | 'jpInvBatch']: boolean },
}

const CarRow = memo(({car, i, action, voyage, showColumns, ...props}: CarRowProps) => {
    const my = useRecoilValue(auth);
    const {t} = useTranslation();
    const {openYardInSingleModal} = useCarModals();
    const {openCertArrivedModal, openUploadExpCertModal, openUploadVehicleInvoiceModal} = useCarDocModals();

    return (
        <TableRow>
            <TableCell sx={{background: `linear-gradient(90deg, ${colors[car.jpInvoice?.package || 0]}aa 0%, ${colors[car.jpInvoice?.package || 0]}44 40%, ${colors[car.jpInvoice?.package || 0]}00 100%)`}}>
                {i}
            </TableCell>

            {showColumns.tallyNum &&
                <TableCell>
                    <EditableInput
                        defaultValue={car.tallyNumber}
                        onChange={value => Api.cars.setTallyNumber(car.id, Number(value))}
                        inputMode="numeric"
                    />
                </TableCell>
            }

            {showColumns.jpInvBatch &&
                <>
                    <TableCell>
                        <EditableInput
                            defaultValue={car.jpInvoice?.batch || ''}
                            onChange={value => Api.cars.setJpInvBatch(car.id, Number(value))}
                            inputMode="numeric"
                        />
                    </TableCell>
                    <TableCell>
                        <EditableInput
                            defaultValue={car.jpInvoice?.pos || ''}
                            onChange={value => Api.cars.setJpInvPos(car.id, Number(value))}
                            inputMode="numeric"
                        />
                    </TableCell>
                </>
            }

            <TableCell>
                {car.model}
                {' '}
                <ProblemMark car={car}/>
            </TableCell>

            <TableCell>
                <CarDocCell
                    car={car}
                    voyage={voyage}
                />
            </TableCell>

            <TableCell>
                <Box display="flex">
                    <CopyableChassis car={car} query={`?voyage=${voyage.id}`}/>
                </Box>
            </TableCell>

            <TableCell>
                {!car.yard?.arrived && (
                    <Tooltip
                        title={t('not_in_yard')}
                        placement="top"
                    >
                        <DirectionsCar
                            color="error"
                            fontSize="small"
                            sx={{verticalAlign: 'middle'}}
                            onClick={() => my.permissions.has('cars manage') && openYardInSingleModal(car)}
                        />
                    </Tooltip>
                )}

                {exportCertificateRequired(car) && <>
                    {!car.documents.exportCertificate && (
                        <Tooltip
                            title={t('no_export_document')}
                            placement="top"
                        >
                            <PictureAsPdf
                                color="error"
                                fontSize="small"
                                sx={{verticalAlign: 'middle'}}
                                onClick={() => my.permissions.has('cars update') && openUploadExpCertModal(car)}
                            />
                        </Tooltip>
                    )}

                    {voyage.pol.id === 1 &&  !car.exportCertificate?.referenceNum && (
                        <Tooltip
                            title={t('no_reference_number')}
                            placement="top"
                        >
                            <WarningAmber
                                color="error"
                                fontSize="small"
                                sx={{verticalAlign: 'middle'}}
                            />
                        </Tooltip>
                    )}

                    {voyage.pol.id === 1 && !car.exportCertificate?.originalArrivedAt && (
                        <Tooltip
                            title={t('no_original_ec')}
                            placement="top"
                        >
                            <Article
                                color="error"
                                fontSize="small"
                                sx={{verticalAlign: 'middle'}}
                                onClick={() => my.permissions.has('cars manage') && openCertArrivedModal(car)}
                            />
                        </Tooltip>
                    )}
                </>}
            </TableCell>

            <TableCell sx={{textAlign: 'right'}}>
                <Currency value={car.unitPrice}/>
                {' '}
                <Box
                    component="span"
                    color={car.priceTerms === PriceTerms.FOB ? 'warning.main' : undefined}
                >
                    {car.priceTerms}
                </Box>
            </TableCell>

            <TableCell>
                {car.fuel}
            </TableCell>

            {showColumns.consignee &&
                <TableCell>
                    <PartyButton
                        car={car}
                        party={car.parties.consignee}
                        type={PartyType.Consignee}
                    />
                </TableCell>
            }

            {showColumns.notify &&
                <TableCell>
                    <PartyButton
                        car={car}
                        party={car.parties.notify}
                        type={PartyType.Notify}
                    />
                </TableCell>
            }

            {showColumns.dealer &&
                <TableCell>
                    <PartyButton
                        car={car}
                        party={car.parties.dealer}
                        type={PartyType.Dealer}
                    />
                </TableCell>
            }

            <TableCell>
                <CarCargoCell
                    car={car}
                    openInnerCargoModal={props.openInnerCargoModal}
                />
            </TableCell>

            <TableCell>
                <Box display="flex">
                    <AlbumButton car={car}/>
                </Box>
            </TableCell>

            {showColumns.freight && (
                <TableCell>
                    {car.prices?.main.invoice && (
                        <PriceWithInvoice
                            price={car.prices!.main.total}
                            currency="jpy"
                            paid={car.prices!.main.paid === car.prices!.main.total}
                            invoice={my.permissions.has('invoices view') ? car.prices!.main.invoice : null}
                        />
                    )}

                    {car.prices?.secondary.total && (
                        <Currency
                            value={car.prices.secondary.total}
                            currency="usd"
                        />
                    )}
                </TableCell>
            )}

            <TableCell align="right">
                {my.permissions.has('cars update') && (car.status === StockStatus.Pending || my.type === CompanyType.Master) && (
                    <IconButton
                        size="small"
                        aria-label="show more actions"
                        onClick={action}
                    >
                        <MoreVert fontSize="small"/>
                    </IconButton>
                )}
            </TableCell>
        </TableRow>
    )
}, (prevProps, nextProps) => (
    prevProps.car === nextProps.car
));

const Voyage = () => {
    return (
        <Suspense fallback={null}>
            <VoyageInner/>
        </Suspense>
    )
};

export default Voyage;
